import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import '../button/run-button.js';
import * as defaultSkin from './skins/icon-button.default.js';
import * as wireframeSkin from './skins/icon-button.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

/** @type {readonly ['xs', 'sm', 'md', 'lg']} */
export const ICON_BUTTON_SIZES = Object.freeze(['xs', 'sm', 'md', 'lg']);

/** @type {readonly ['ghost', 'outline', 'filled']} */
export const ICON_BUTTON_VARIANTS = Object.freeze(['ghost', 'outline', 'filled']);

const DEFAULT_SIZE = 'md';
const DEFAULT_VARIANT = 'ghost';

class RunIconButton extends RunElement {
  #disabled = false;
  #loading = false;
  #size = DEFAULT_SIZE;
  #variant = DEFAULT_VARIANT;
  #icon = '';
  #label = '';
  #rendering = false;

  static get observedAttributes() {
    return ['disabled', 'loading', 'size', 'variant', 'icon', 'label'];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#warnIfNoLabel();
    this.requestUpdate();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'disabled': this.disabled = newValue !== null; break;
      case 'loading':  this.loading  = newValue !== null; break;
      case 'size':     this.size     = newValue;          break;
      case 'variant':  this.variant  = newValue;          break;
      case 'icon':     this.icon     = newValue;          break;
      case 'label':    this.label    = newValue;          break;
    }
  }

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.requestUpdate();
  }

  get loading() { return this.#loading; }
  set loading(value) {
    const coerced = Boolean(value);
    if (this.#loading === coerced) return;
    this.#loading = coerced;
    this.toggleAttribute('loading', coerced);
    this.requestUpdate();
  }

  get size() { return this.#size; }
  set size(value) {
    const coerced = String(value ?? '').trim();
    const resolved = ICON_BUTTON_SIZES.includes(coerced) ? coerced : DEFAULT_SIZE;
    if (this.#size === resolved) return;
    this.#size = resolved;
    this.setAttribute('size', resolved);
    this.requestUpdate();
  }

  get variant() { return this.#variant; }
  set variant(value) {
    const coerced = String(value ?? '').trim();
    const resolved = ICON_BUTTON_VARIANTS.includes(coerced) ? coerced : DEFAULT_VARIANT;
    if (this.#variant === resolved) return;
    this.#variant = resolved;
    this.setAttribute('variant', resolved);
    this.requestUpdate();
  }

  get icon() { return this.#icon; }
  set icon(value) {
    const coerced = String(value ?? '').trim();
    if (this.#icon === coerced) return;
    this.#icon = coerced;
    this.setAttribute('icon', coerced);
    this.requestUpdate();
  }

  get label() { return this.#label; }
  set label(value) {
    const coerced = String(value ?? '').trim();
    if (this.#label === coerced) return;
    this.#label = coerced;
    this.setAttribute('label', coerced);
    this.#warnIfNoLabel();
    this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (!this.shadowRoot) return;
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
  }

  #resolveSkin() {
    const s = this.getAttribute('skin');
    if (s) return s;
    return resolveSkinFromAncestors(this);
  }

  #warnIfNoLabel() {
    if (!this.#label) {
      // Required a11y contract: every icon-only button needs an accessible label.
      console.error(
        '[run-icon-button] Missing required "label" attribute — add label="<description of action>" ' +
        'to provide an aria-label for screen readers.',
      );
    }
  }
}

if (!customElements.get('run-icon-button')) {
  customElements.define('run-icon-button', RunIconButton);
}

export { RunIconButton };
export default RunIconButton;
